import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  ErrorData,
  ERRORS_TABLE_PAGINATION_LIMIT,
  ImportSourceType,
  OPTIONS,
  SpreadsheetBulkImportPreferences,
  TaskState,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import preferences from 'Preferences';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Clipboard from 'ui-library/Clipboard';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as ExportUtil from 'util/ExportUtil';

export enum SpreadsheetType {
  SPREADSHEET = 'SPREADSHEET',
  STRUCTURED_TABLE = 'STRUCTURED_TABLE',
}

export function getSpreadsheetIndexOffset(spreadsheetType: SpreadsheetType): number {
  switch (spreadsheetType) {
    case SpreadsheetType.SPREADSHEET:
      // We add 2 to the displayed rowIndex because errors are returned 0-indexed and the backend disregards
      // the header row
      return 2;
    case SpreadsheetType.STRUCTURED_TABLE:
      return 1;
    default:
      throw new Error(`Unexpected spreadsheet type ${spreadsheetType}`);
  }
}

function _getTSVErrors(
  spreadsheetType: SpreadsheetType,
  errorMessages: Map<number, Array<SpreadsheetBulkImportUtil.SpreadsheetValidationErrorMessage>>
): string {
  const rows = [];
  rows.push(['Row #', 'Message', 'Status']);
  for (const [row, rowErrors] of errorMessages) {
    for (const error of rowErrors) {
      let statusText;
      if (error.resolved) {
        statusText = 'Resolved';
      } else if (error.icon === 'warning') {
        statusText = 'Warning';
      } else {
        assert(!error.resolved && error.icon === 'error', 'Unexpected error message status');
        statusText = 'Error';
      }
      rows.push([`${row + getSpreadsheetIndexOffset(spreadsheetType)}`, error.message, statusText]);
    }
  }
  return ExportUtil.formatRows(rows, '\t');
}

function ValidationErrorsTableContainer(props: {
  children?: React.ReactNode;
  copyableErrors: string | null;
  emptyTableText?: string;
  errorData: {state: TaskState.FAILED} | ErrorData | null;
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean};
  spreadsheetType?: SpreadsheetType;
}): JSX.Element {
  function showInvalidMoleculeDownload(): boolean {
    return (
      props.errorData?.state === TaskState.COMPLETED &&
      SpreadsheetBulkImportUtil.hasUnresolvedErrors(props.errorData, props.resolvableErrorStatuses) &&
      preferences.get(SpreadsheetBulkImportPreferences.SOURCE_TYPE) === ImportSourceType.SDF
    );
  }

  // Pagination offset
  const [pageOffset, setOffset] = useState<number>(0);

  let formattedErrors: Array<{
    row: number;
    unresolvedMessage: Array<JSX.Element>;
    resolvedMessage: Array<JSX.Element>;
  }> = [];
  if (
    props.errorData != null &&
    props.errorData?.state !== TaskState.FAILED &&
    SpreadsheetBulkImportUtil.hasErrors(props.errorData)
  ) {
    formattedErrors = SpreadsheetBulkImportUtil.getFormattedErrors(
      props.errorData,
      props.resolvableErrorStatuses
    );
  }
  return (
    <div {...getTestProps({component: 'BulkImportModal', element: 'table'})}>
      <Table
        className={classNames(
          'bulkImportModal-table-body u-no-spacing-bottom',
          css(styles.table),
          // Set the height for all pages to be fixed if we have pagination to avoid the pagination controls
          // jumping at the last page which may have less items
          formattedErrors.length > ERRORS_TABLE_PAGINATION_LIMIT ? css(styles.tableFullHeight) : null
        )}
        numRows={Math.min(formattedErrors.length - pageOffset, ERRORS_TABLE_PAGINATION_LIMIT)}
        setOffset={setOffset}
        emptyTableText={props.emptyTableText}
        // The following props make the table scrollable with a fixed header and a max-height
        isCompact={true}
        fitContent={true}
        frozenHeader={true}
        overflowVisible={true}
        showBorder={false}
        tableLayout="fixed"
      >
        <Column
          header={<Cell className={css(styles.rowNumberCell)}>Row #</Cell>}
          renderCell={(rowIndex) => (
            <Cell className={css(styles.rowNumberCell)}>
              {formattedErrors[pageOffset + rowIndex].row + getSpreadsheetIndexOffset(props.spreadsheetType!)}
            </Cell>
          )}
        />
        <Column
          header={
            <Cell className={classNames(css(styles.messageCell))}>
              <div className="u-vertical-center-items">
                Message
                {
                  // Show "Copy errors" button only if there are errors to copy
                  props.copyableErrors != null && (
                    <div className={css(styles.copyableErrors)}>
                      <Clipboard value={props.copyableErrors}>
                        <a>Copy errors</a>
                      </Clipboard>
                    </div>
                  )
                }
                {
                  // Show "Download invalid molecules" button only if there are unresolved errors and the source
                  // type is SDF
                  showInvalidMoleculeDownload() && (
                    <div className="u-padding-left">
                      <Button
                        buttonStyle={ButtonStyle.LINK}
                        text="Download invalid molecules"
                        onClick={() => {
                          SpreadsheetBulkImportActions.downloadInvalidMolecules(
                            props.resolvableErrorStatuses
                          );
                        }}
                      />
                    </div>
                  )
                }
              </div>
            </Cell>
          }
          renderCell={(rowIndex) => (
            <Cell className={classNames(css(styles.messageCell), css(styles.errorText))}>
              {formattedErrors[pageOffset + rowIndex].unresolvedMessage.length > 0 &&
                formattedErrors[pageOffset + rowIndex].unresolvedMessage}
              <span className="faded u-strike-through">
                {formattedErrors[pageOffset + rowIndex].resolvedMessage}
              </span>
            </Cell>
          )}
        />
      </Table>
      {
        // Show pagination only if there are more than ERRORS_TABLE_PAGINATION_LIMIT errors
        formattedErrors.length > ERRORS_TABLE_PAGINATION_LIMIT && (
          <ResultsPager
            setOffset={setOffset}
            pagination={{
              offset: pageOffset,
              limit: ERRORS_TABLE_PAGINATION_LIMIT,
              total: formattedErrors.length,
            }}
            isLoading={false}
          />
        )
      }
    </div>
  );
}

export interface ValidationErrorsTableProps {
  spreadsheetType: SpreadsheetType;
  errorData: {state: TaskState.FAILED} | ErrorData | null;
  nextButtonText?: string;
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean};
  selectedTagSchemaId?: string | null;
}

/**
 * Renders validation errors for a spreadsheet from errorData.
 *
 * If there are no errors, show a CTA to continue to the next step.
 */
export default function ValidationErrorsTable(props: ValidationErrorsTableProps): JSX.Element | null {
  const {spreadsheetType, errorData, nextButtonText, resolvableErrorStatuses} = props;

  if (!errorData) {
    return null;
  }
  let emptyTableText: string;
  if (errorData.state === TaskState.FAILED || !SpreadsheetBulkImportUtil.hasErrors(errorData)) {
    emptyTableText =
      errorData.state === TaskState.FAILED // If the task failed, show an error message
        ? 'An error occurred while importing the file. Please try again.'
        : nextButtonText // If there are no errors, show message to continue to the next step
          ? `There were no errors, click ${props.nextButtonText} to continue.`
          : 'There were no errors.';
    return (
      <ValidationErrorsTableContainer
        emptyTableText={emptyTableText}
        copyableErrors={null}
        errorData={null}
        resolvableErrorStatuses={{}}
      />
    );
  }

  const errorMessages = SpreadsheetBulkImportUtil.getErrorMessages(errorData, resolvableErrorStatuses);

  if (
    !assertAndContinue(
      SpreadsheetBulkImportUtil.hasErrors(errorData),
      'Expected simple validation errors table to have errors'
    )
  ) {
    return null;
  }

  return (
    <ValidationErrorsTableContainer
      copyableErrors={_getTSVErrors(spreadsheetType, errorMessages)}
      errorData={errorData}
      resolvableErrorStatuses={resolvableErrorStatuses}
      spreadsheetType={spreadsheetType}
    />
  );
}

const styles = StyleSheet.create({
  rowNumberCell: {
    width: '12% !important',
    whiteSpace: 'pre-wrap',
  },
  messageCell: {
    width: '100% !important',
    whiteSpace: 'pre-wrap',
  },
  copyableErrors: {
    marginLeft: 'auto',
  },
  errorText: {
    whiteSpace: 'pre-wrap',
    // Error messages may sometimes have DNA sequences, which are long words without spaces, so we should be
    // willing to split those for wrapping purposes, capped at a max height per row and scrollable when past
    // that height.
    overflowWrap: 'anywhere',
    maxHeight: 100,
    overflowY: 'auto',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '40vh',
  },
  tableFullHeight: {
    height: '40vh',
  },
});
